import { useLazyQuery, useMutation } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { GenerateSharedAccessSignatureQuery } from '@deltek/specification-client/core/GraphQL/Queries/generateSharedAccessSignature.queries'
import {
    GenerateSharedAccessSignatureInput,
    GenerateSharedAccessSignatureResponse,
} from '@deltek/specification-client/core/Models/generateSharedAccessSignature'
import { ProfileImageInput } from '@deltek/specification-client/core/Models/profileImageInput'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { suffixDimension } from '@deltek/specification-client/core/Utilities/stringUtils'
import {
    PickerFileMetadata,
    PickerResponse,
} from '@deltek/specification-client/core/Widgets/Filestack/FilestackButton'
import ImageFilestackButton from '@deltek/specification-client/core/Widgets/Filestack/ImageFilestackButton'
import SpecImage from '@deltek/specification-client/core/Widgets/SpecImage'
import { Theme, makeStyles } from '@material-ui/core/styles'
import AddAPhotoIcon from '@material-ui/icons/AddAPhoto'
import Skeleton from '@material-ui/lab/Skeleton'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import React, { useEffect, useState } from 'react'
import CircularPencilButton from './CircularPencilButton'

const companyLogoStyles = makeStyles((theme: Theme) => ({
    companyLogoHeightWidth: {
        width: 200,
        height: 60,
    },
    companyLogoImg: {
        objectFit: 'scale-down',
        objectPosition: 'left',
    },
    imageWidthPosition: {
        width: '100%',
        position: 'absolute',
    },
    addCompanyLogoPlaceholder: {
        backgroundColor: '#A2CDDD',
        color: '#5E5F61',
        textAlign: 'center',
    },
    editButton: {
        position: 'absolute',
        marginTop: 10,
        marginRight: 6,
        right: theme.spacing(0.5),
    },
}))

export interface CompanyLogoProps {
    logoUrl?: string
    companyLogoFileInTransit: boolean
    handleCompanyLogoChange?: (companyLogoImageInput: ProfileImageInput) => void
    handleCompanyLogoFileInTransit: (imageRendering: boolean) => void
    handleFilestackLoading?: (filestackLoading: boolean | null) => void
}

const CompanyLogoEdit: React.FC<CompanyLogoProps> = ({
    logoUrl = '',
    companyLogoFileInTransit,
    handleCompanyLogoChange,
    handleCompanyLogoFileInTransit,
    handleFilestackLoading,
}) => {
    const classes = companyLogoStyles()
    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)
    const [imageFile, setImageFile] = useState<PickerFileMetadata>()
    const [fullImagePath, setFullImagePath] = useState<string>()
    const [computedImageSrc, setComputedImageSrc] = useState<string | null>(
        null
    )
    const [
        generateSharedAccessSignature,
        { loading: sasLoading, error: sasError, data: sasData },
    ] = useLazyQuery<
        GenerateSharedAccessSignatureResponse,
        GenerateSharedAccessSignatureInput
    >(GenerateSharedAccessSignatureQuery, {
        fetchPolicy: 'no-cache',
    })
    const [specImageLoading, setSpecImageLoading] = useState<boolean>(false)

    const onFilepickerDone = (pickerResponse: PickerResponse) => {
        if (pickerResponse.filesFailed.length) {
            showGlobalSnackbar?.({
                variables: {
                    id: 'CompanyBannerFileUploadError',
                    message: 'Error uploading company logo.',
                    variant: 'error',
                },
            })
            return
        }

        setComputedImageSrc('')
        generateSharedAccessSignature({
            variables: {
                input: {
                    durationInMinutes: 5,
                    storageAccountName:
                        processEnv.REACT_APP_BlobStorageAccountName,
                },
            },
        })
        setImageFile(pickerResponse.filesUploaded[0])
    }

    useEffect(() => {
        if (!sasError) return
        showGlobalSnackbar({
            variables: {
                id: 'CompanyLogoEditSas',
                message: 'Error getting shared access signature.',
                variant: 'error',
            },
        })
    }, [sasError, showGlobalSnackbar])

    useEffect(() => {
        if (!imageFile || !sasData) return
        const imagePath = `${processEnv.REACT_APP_BlobStorageUrl}/${
            processEnv.REACT_APP_BlobStorageUnverifiedContainerName
        }/${encodeURIComponent(imageFile.key!)}${
            sasData.generateSharedAccessSignature.sharedAccessSignature
        }`
        setFullImagePath(imagePath)
        handleCompanyLogoChange?.({
            fileName: imageFile.filename,
            key: imageFile.key!,
            fileSize: imageFile.size,
            resizeDimensions: [{ percentage: 50 }, { percentage: 25 }],
        })
    }, [imageFile, sasData, handleCompanyLogoChange])

    useEffect(() => {
        if (fullImagePath) {
            setComputedImageSrc(fullImagePath)
            return
        }

        if (logoUrl) {
            setComputedImageSrc(
                suffixDimension({
                    filename: logoUrl,
                    dimension: { percentage: 50 },
                })
            )
        }
    }, [logoUrl, fullImagePath])

    return (
        <div
            style={{ position: 'relative' }}
            className={classes.companyLogoHeightWidth}
            data-testid="companyLogoEditContainer"
        >
            {(companyLogoFileInTransit || sasLoading || specImageLoading) && (
                <Skeleton
                    data-testid="companyLogoEditSkeleton"
                    variant="rect"
                    className={clsx(
                        classes.companyLogoHeightWidth,
                        classes.imageWidthPosition
                    )}
                />
            )}
            {!companyLogoFileInTransit && !sasLoading && (
                <>
                    {computedImageSrc !== null ? (
                        <SpecImage
                            alt="uploaded company logo"
                            src={computedImageSrc}
                            additionalClassNames={clsx(
                                classes.imageWidthPosition,
                                classes.companyLogoHeightWidth,
                                classes.companyLogoImg
                            )}
                            data-testid="companyLogoEditImg"
                            handleImageLoading={setSpecImageLoading}
                        />
                    ) : (
                        <GridContainer
                            direction="column"
                            justifyContent="center"
                            alignItems="center"
                            className={clsx(
                                classes.addCompanyLogoPlaceholder,
                                classes.imageWidthPosition
                            )}
                            style={{ height: 'inherit' }}
                        >
                            <GridItem>
                                <AddAPhotoIcon />
                                <br />
                                Company Logo
                            </GridItem>
                        </GridContainer>
                    )}
                </>
            )}
            <ImageFilestackButton
                apiKey={processEnv.REACT_APP_FileStack_ApiKey}
                kpCoreUri={processEnv.REACT_APP_KPCoreUri}
                onFilepickerDone={onFilepickerDone}
                showGlobalSnackbar={showGlobalSnackbar}
                handleLoadingFilestack={handleFilestackLoading}
                handleTransferringFiles={handleCompanyLogoFileInTransit}
                renderButton={(onClick: (event: React.MouseEvent) => void) => (
                    <CircularPencilButton
                        ariaLabel="Upload cover logo button"
                        data-testid="editLogoImageContainer"
                        containerClassName={classes.editButton}
                        hide={
                            companyLogoFileInTransit ||
                            sasLoading ||
                            specImageLoading
                        }
                        onClick={onClick}
                    />
                )}
            />
        </div>
    )
}

export default CompanyLogoEdit
